import { rayCapsule } from './geometry.js';
import { poseCapsule } from './state.js';
import { defOf, mech, targetPose } from './tick.js';
import { otherSlot } from './types.js';
/** Hittable radius of a deployable, or 0 for kinds that shots pass through. */
export function deployableHitRadius(d) {
    switch (d.kind) {
        case 'turret':
            return 1.1;
        case 'drone':
            return 0.7;
        default:
            return 0;
    }
}
/** Centre of the hittable sphere of a deployable. */
export function deployableHitCenter(d) {
    return { x: d.position.x, y: d.position.y + (d.kind === 'turret' ? 1.1 : 0), z: d.position.z };
}
function raySphere(origin, dir, c, r) {
    const lx = origin.x - c.x;
    const ly = origin.y - c.y;
    const lz = origin.z - c.z;
    const b = lx * dir.x + ly * dir.y + lz * dir.z;
    const cc = lx * lx + ly * ly + lz * lz - r * r;
    const disc = b * b - cc;
    if (disc < 0)
        return null;
    const t = -b - Math.sqrt(disc);
    return t >= 0 ? t : null;
}
export function findDeployable(t, blockerId) {
    if (blockerId === null)
        return null;
    const id = Number(blockerId);
    return t.s.deployables.find((d) => d.id === id) ?? null;
}
export function traceShot(t, shooter, origin, dir, maxDistance, options = {}) {
    const width = options.width ?? 0;
    const arena = t.world.castRayHit(origin, dir, maxDistance);
    let limit = maxDistance;
    let barrier = null;
    let crossed = null;
    let blocked = false;
    let normal = null;
    if (arena) {
        barrier = findDeployable(t, arena.blockerId);
        if (barrier && options.pierce) {
            crossed = barrier;
        }
        else {
            limit = arena.distance;
            blocked = true;
            normal = arena.normal;
        }
    }
    let best = limit;
    let hit = null;
    let struck = null;
    const enemy = otherSlot(shooter);
    const target = mech(t, enemy);
    const pose = options.rewind === false ? target.body : targetPose(t, shooter);
    if (target.alive) {
        const cap = poseCapsule(pose, defOf(t, enemy));
        const widened = {
            base: cap.base,
            radius: cap.radius + width,
            halfHeight: cap.halfHeight,
        };
        const d = rayCapsule(origin, dir, best, widened);
        if (d !== null) {
            best = d;
            hit = enemy;
        }
    }
    for (const dep of t.s.deployables) {
        if (dep.slot === shooter)
            continue;
        const r = deployableHitRadius(dep);
        if (r <= 0)
            continue;
        const d = raySphere(origin, dir, deployableHitCenter(dep), r + width);
        if (d !== null && d < best) {
            best = d;
            hit = null;
            struck = dep;
        }
    }
    if (hit === null && struck === null) {
        struck = blocked ? barrier : null;
    }
    else {
        normal = null;
        blocked = false;
    }
    // A pierced barrier is reported when it lies in front of whatever was hit.
    if (struck === null && crossed && arena && arena.distance <= best)
        struck = crossed;
    return {
        distance: best,
        point: { x: origin.x + dir.x * best, y: origin.y + dir.y * best, z: origin.z + dir.z * best },
        normal,
        hit,
        deployable: struck,
        blocked,
        targetPose: pose,
    };
}
//# sourceMappingURL=trace.js.map